import classNames from 'classnames/bind';
import styles from './Dashboard.module.scss';
import { Box } from '@mui/material';
import { memo, useEffect, useState } from 'react';
import dashboardApi from '~/apis/dashboard';
import { IAlert24hData, IGetAreaStats, IGetNewPersonStats, IDashboardParams } from '~/utils/interface/dashboard';
import DevicesSummary from './components/DevicesSummary';
import { cast, handleApiError, isTypeRoleId } from '~/utils/helper';
import BaseRow from '~/components/common/base-row/BaseRow';
import Alert24hSummary from './components/Alert24hSummary';
import RecentAlert from './components/RecentAlert';
import { useAppSelector } from '~/redux/hooks';
import AlertStatsSummary from './components/AlertStatsSummary';
import AreaStatSummary from './components/AreaStatsSummary';
import { AreaStatsEnum, NewPersonStatsEnum, RoleIdUserEnum } from '~/utils/enum';
import NewPersonStats from './components/NewPersonStats';

const cx = classNames.bind(styles);

const Dashboard = () => {
  const { selectedUser, userProfile } = useAppSelector((state) => state.auth);
  const username = selectedUser?.username ?? userProfile?.username;
  const currentUser = Object.values(selectedUser).length === 0 ? userProfile : selectedUser;

  const [alert24hData, setAlert24hData] = useState<Array<IAlert24hData>>([]);
  const [areaStatsData, setAreaStatsData] = useState<Array<IGetAreaStats>>();
  const [newStatsData, setNewStatsData] = useState<Array<IGetNewPersonStats>>();

  useEffect(() => {
    if (!currentUser?.username) return;

    (async () => {
      await Promise.all([
        getAlert24h({ username: currentUser?.username }),
        getAreaStats({ username: currentUser?.username, type: AreaStatsEnum.PERSON }),
        getNewPersonStats({ username: currentUser?.username, type: NewPersonStatsEnum.MONTH }),
      ]);
    })();
  }, [currentUser?.username]);

  const getAlert24h = async (params: IDashboardParams) => {
    try {
      const response = await dashboardApi.getAlert24h(params);
      setAlert24hData(response.data.result);
    } catch (error) {
      handleApiError(error);
    }
  };

  const getAreaStats = async (params: IDashboardParams) => {
    try {
      const response = await dashboardApi.getAreaStats(params);
      setAreaStatsData(response.data.result.data);
    } catch (error) {
      handleApiError(error);
    }
  };

  const getNewPersonStats = async (params: IDashboardParams) => {
    try {
      const response = await dashboardApi.getNewPersonStats(params);
      setNewStatsData(response.data.result.data);
    } catch (error) {
      handleApiError(error);
    }
  };

  const hangleChangeAreStats = async (value: string) => {
    try {
      await getAreaStats({ username, type: value });
    } catch (error) {
      handleApiError(error);
    }
  };

  const hangleChangeNewPersonStats = async (value: string) => {
    try {
      await getNewPersonStats({ username, type: value });
    } catch (error) {
      handleApiError(error);
    }
  };

  return (
    <Box className={cx('dashboard-container')}>
      <BaseRow className='gap-5 mb-5 max-[1699px]:flex-row max-[1699px]:flex-wrap'>
        <Box className='w-full'>
          <BaseRow className='gap-5 mb-5'>
            <Box className='w-full'>
              <DevicesSummary currentUser={currentUser} />
            </Box>
            <Box className='w-full'>
              <AlertStatsSummary currentUser={currentUser} />
            </Box>
          </BaseRow>
          <Box className='w-full'>
            <Alert24hSummary data={alert24hData} currentUser={currentUser} />
          </Box>
        </Box>
        <Box className='min-[1699px]:w-2/3 w-full'>
          <RecentAlert />
        </Box>
      </BaseRow>
      {!isTypeRoleId(cast(currentUser?.roleid), RoleIdUserEnum.XA_PHUONG) && (
        <BaseRow className='gap-5 mb-5 max-[1699px]:flex-row max-[1699px]:flex-wrap'>
          <Box className='w-full'>
            <AreaStatSummary data={areaStatsData} onChange={hangleChangeAreStats} />
          </Box>
          <Box className='w-full'>
            <NewPersonStats data={newStatsData} onChange={hangleChangeNewPersonStats} />
          </Box>
        </BaseRow>
      )}
    </Box>
  );
};

export default memo(Dashboard);
